import React, { useState } from 'react';
import {
  Edit,
  usePermissions,
  useNotify,
  useRefresh,
  ListBase,
  useRecordContext,
  TabbedShowLayout,
} from 'react-admin';

import {
  Card,
} from '@mui/material';

import { StyledTab, StyledTabs } from '../layout/Tab';
import SubNavigation from '../layout/SubNavigation';

import GeneralInfo from './editTabs/data/general';
import VehicleInspection from './editTabs/data/vehicleInspection';
import PreviousOwners from './editTabs/data/previousOwners';
import Overview from './editTabs/overview';

import hasAccess from '../../utilities/hasAccess';

import CarBreadcrumb from './CarBreadcrumb';
import actions, { Actions } from '../../constants/actions';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import ReportModal from '../../customFields/ReportModal';
import reportTypes from '../../constants/reportTypes';
import { ICar } from '../../utilities/schemas/car';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import AuditResourceList from '../audit/AuditResourceList';
import EditToolbar from '../layout/EditToolbar';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';

const EditActions = () => (
  <EditToolbar>
    <ReportModal />
  </EditToolbar>
);

export const CarEditContent = () => {
  const { permissions } = usePermissions<Actions[]>();
  const [viewTab, setViewTab] = useState(0);
  const record = useRecordContext<ICar>();

  const handleViewTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setViewTab(newValue);
  };

  if (!record) return null;

  return (
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="Overview">
        <Overview />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Data">
        <SubNavigation>
          <StyledTabs value={viewTab} onChange={handleViewTabChange}>
            <StyledTab label="General" value={0} />
            <StyledTab label="Previous owners" value={1} />
            <StyledTab label="Vehicle inspection" value={2} />
          </StyledTabs>
        </SubNavigation>
        {viewTab === 0 && <GeneralInfo />}
        {viewTab === 1 && <PreviousOwners />}
        {viewTab === 2 && <VehicleInspection />}
      </TabbedShowLayout.Tab>
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
          <AuditResourceList resourceType={resourceTypes.CAR} />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
      }) && (
        <TabbedShowLayout.Tab label="Reports" path="reports">
          <ListBase
            storeKey={false}
            disableSyncWithLocation
            perPage={5}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filter={{ type: reportTypes.ENTITY_REPORT, entityId: record.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </ListBase>
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const CarEdit = () => {
  const notify = useNotify();
  const refresh = useRefresh();

  const onSuccess = () => {
    notify('Car updated');
    refresh();
  };

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
      actions={<EditActions />}
    >
      <CarBreadcrumb />
      <CarEditContent />
    </Edit>
  );
};

export default CarEdit;
